'use es6';

import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragSource } from 'react-dnd-compat';
import { getDefaultType, getDisplayName, getModuleIcon, getIsModuleEditable, getIsGlobalModuleGroupOrPartial, getModuleById } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getIsAdvancedModuleWithoutAccess, getModuleHasPaymentsFieldWithoutAccess, getModuleHasPersonalizationTokensWithoutAccess } from 'ContentEditorUI/redux/selectors/downgradeSelectors';
import { getIsModuleStyleable } from 'ContentEditorUI/redux/selectors/styleSelectors';
import { BaseResponsiveToolbar, buildModuleEditAction } from './BaseResponsiveToolbar';
import { dragTypes } from 'InpageEditorUI/lib/layoutSection/constants';
import { logLayoutSectionEvent } from 'InpageEditorUI/eventsAsActions/layoutTransitionEventsAsActions';
import { getIsInDefaultBreakpointMode, getIsInNonDefaultBreakpointMode } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { getSelectedItemId } from 'ContentEditorUI/redux/selectors/selectionSelectors';
import { useBus } from '../../context/Bus';
import { clonePostBodyModule, deletePostBodyModule, cloneModuleInRichText, deleteModuleInRichText } from 'ContentEditorUI/redux/actions/moduleActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getIsDataAuthoringModeActive } from 'ContentEditorUI/redux/selectors/instanceEditingSelectors';
import { getResponsiveToolbarPinLocation } from './helpers';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { DuplicateIcon } from 'foundations-assets/icons/components/DuplicateIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const dragSource = {
  beginDrag({
    id,
    parentId,
    moduleIcon
  }) {
    const dragItem = {
      dragId: id,
      dragParentId: parentId,
      iconUrl: moduleIcon
    };
    return dragItem;
  },
  canDrag(props) {
    return props.isDraggable;
  }
};
const dragCollect = monitor => ({
  isDragging: monitor.isDragging()
});
const useDndInfo = () => {
  const isInDefaultBreakpointMode = useSelector(getIsInDefaultBreakpointMode);
  return {
    isInDefaultBreakpointMode
  };
};
const DndBaseResponsiveToolbar = DragSource(dragTypes.EXISTING_ITEM, dragSource, dragCollect)(BaseResponsiveToolbar);
const trackAction = (event, moduleInfo, bus) => {
  const {
    moduleType,
    displayName: moduleLabel
  } = moduleInfo;
  bus.dispatchActionToEventBus(logLayoutSectionEvent(event, {
    action: 'Blog Drag and Drop Action',
    event,
    moduleType,
    moduleLabel
  }));
};
const makeEditAction = (bus, dispatch, moduleInfo) => {
  return buildModuleEditAction({
    moduleId: moduleInfo.id,
    isEditable: moduleInfo.isEditable,
    isGlobal: moduleInfo.isGlobal,
    bus,
    dispatch,
    moduleType: moduleInfo.moduleType,
    isInNonDefaultBreakpointMode: moduleInfo.isInNonDefaultBreakpointMode
  });
};
const makeDeleteAction = (bus, dispatch, moduleInfo, isDataAuthoringModeActive) => {
  const deleteAction = isDataAuthoringModeActive ? deleteModuleInRichText : deletePostBodyModule;
  const actionName = isDataAuthoringModeActive ? 'deleted-module-in-rte' : 'deleted-post-body-module';
  return {
    id: 'module-delete',
    icon: DeleteIcon,
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.delete"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.delete"
    }),
    callback: () => {
      dispatch(deleteAction(moduleInfo.module));
      trackAction(actionName, moduleInfo, bus);
    },
    placement: 'top left'
  };
};
const makeCloneAction = (bus, dispatch, moduleInfo, isDataAuthoringModeActive) => {
  const cloneAction = isDataAuthoringModeActive ? cloneModuleInRichText : clonePostBodyModule;
  const actionName = isDataAuthoringModeActive ? 'cloned-module-in-rte' : 'cloned-post-body-module';
  return {
    id: 'module-duplicate',
    icon: DuplicateIcon,
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.clone"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.clone"
    }),
    callback: () => {
      dispatch(cloneAction(moduleInfo.module));
      trackAction(actionName, moduleInfo, bus);
    }
  };
};
const useToolBarActions = (moduleInfo, isDataAuthoringModeActive) => {
  const dispatch = useDispatch();
  const bus = useBus();
  return useMemo(() => [makeEditAction(bus, dispatch, moduleInfo, isDataAuthoringModeActive), makeCloneAction(bus, dispatch, moduleInfo, isDataAuthoringModeActive), makeDeleteAction(bus, dispatch, moduleInfo, isDataAuthoringModeActive)], [dispatch, bus, moduleInfo, isDataAuthoringModeActive]);
};
const useModuleInfo = id => {
  const module = useSelector(state => getModuleById(state, id));
  const moduleType = useSelector(state => getDefaultType(state, id));
  const moduleIcon = useSelector(state => getModuleIcon(state, id));
  const displayName = useSelector(state => getDisplayName(state, id));
  const isEditable = useSelector(state => getIsModuleEditable(state, id));
  const isStyleable = useSelector(state => getIsModuleStyleable(state, id));
  const isGlobal = useSelector(state => getIsGlobalModuleGroupOrPartial(state, id));
  const isAdvancedModuleWithoutAccess = useSelector(state => getIsAdvancedModuleWithoutAccess(state, module));
  const isPaymentsModuleWithoutAccess = useSelector(state => getModuleHasPaymentsFieldWithoutAccess(state, module));
  const hasPersonalizationTokenWithoutAccess = useSelector(state => getModuleHasPersonalizationTokensWithoutAccess(state, module));
  const selectedItemId = useSelector(state => getSelectedItemId(state));
  const isInNonDefaultBreakpointMode = useSelector(getIsInNonDefaultBreakpointMode);
  return {
    id,
    module,
    moduleType,
    moduleIcon,
    displayName,
    isEditable,
    isStyleable,
    isGlobal,
    isAdvancedModuleWithoutAccess,
    hasPersonalizationTokenWithoutAccess,
    isPaymentsModuleWithoutAccess,
    selectedItemId,
    isInNonDefaultBreakpointMode,
    parentId: module ? module.get('parentId') : null
  };
};
export const ResponsiveToolbarInRichText = ({
  id,
  responsiveSize,
  isFixedPosition,
  top,
  left,
  toolbarHeight,
  onHandleMouseDown,
  onDropdownOpenChange,
  connectDragSource
}) => {
  const moduleInfo = useModuleInfo(id);
  const {
    isInDefaultBreakpointMode
  } = useDndInfo();
  const isDataAuthoringModeActive = useSelector(getIsDataAuthoringModeActive);
  const actions = useToolBarActions(moduleInfo, isDataAuthoringModeActive);
  const isSelectedItem = id === moduleInfo.selectedItemId;
  return /*#__PURE__*/_jsx(DndBaseResponsiveToolbar, {
    id: id,
    position: isFixedPosition ? 'fixed' : 'absolute',
    pinLocation: getResponsiveToolbarPinLocation(top, toolbarHeight, isSelectedItem),
    moduleIcon: moduleInfo.moduleIcon,
    actions: actions,
    isGlobal: moduleInfo.isGlobal,
    onHandleMouseDown: onHandleMouseDown,
    onDropdownOpenChange: onDropdownOpenChange,
    insetBlockStart: `${Math.round(top)}px`,
    insetInlineStart: `${Math.round(left)}px`,
    responsiveSize: responsiveSize,
    handleTooltip: moduleInfo.displayName,
    isAdvancedModuleWithoutAccess: moduleInfo.isAdvancedModuleWithoutAccess,
    isPaymentsModuleWithoutAccess: moduleInfo.isPaymentsModuleWithoutAccess,
    hasPersonalizationTokenWithoutAccess: moduleInfo.hasPersonalizationTokenWithoutAccess,
    isSelectedItem: isSelectedItem,
    connectDragSource: connectDragSource,
    parentId: moduleInfo.parentId,
    isDraggable: isInDefaultBreakpointMode
  });
};
export const MockMobileResponsiveToolbarInRichText = ({
  id,
  responsiveSize
}) => {
  const moduleInfo = useModuleInfo(id);
  const actions = useToolBarActions(moduleInfo);
  return /*#__PURE__*/_jsx(BaseResponsiveToolbar, {
    actions: actions,
    responsiveSize: responsiveSize
  });
};
export const MockDesktopResponsiveToolbarInRichText = ({
  id,
  responsiveSize
}) => {
  const moduleInfo = useModuleInfo(id);
  const actions = useToolBarActions(moduleInfo);
  return /*#__PURE__*/_jsx(BaseResponsiveToolbar, {
    actions: actions,
    responsiveSize: responsiveSize
  });
};